/**
 * ChamadoFinalizarApplyCard — o fechamento do chamado que o @Atendente redigiu.
 *
 * O Atendente só REDIGE (`chamado_finalizar`). Quem clica em Finalizar é o atendente do chamado,
 * e a escrita é dele: o texto EDITADO aqui vai ao solicitante como comentário e o chamado fica
 * aguardando a validação dele. O servidor revalida o gate (`pode_atuar(chamados, assign)`).
 */
import { useState } from 'react'
import { Badge, Button, Card, Group, Text, Textarea, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCircleCheck } from '@tabler/icons-react'
import { formatApiError } from '@/api/errors'
import { useCan } from '@/store/auth'
import { useApplyChamadoAction, useDeclineAction } from '@/hooks/useChat'
import type { ProposedAction } from './ActionProposalCard'

interface Props {
  conversationId: number
  messageId: number
  proposedAction: ProposedAction
}

const STATUS_LABELS: Record<ProposedAction['status'], string> = {
  pending: 'Pendente',
  executed: 'Finalizado',
  failed: 'Falhou',
  declined: 'Recusado',
}
const STATUS_COLORS: Record<ProposedAction['status'], string> = {
  pending: 'yellow',
  executed: 'teal',
  failed: 'red',
  declined: 'gray',
}

const ERROS: Record<string, string> = {
  texto_vazio: 'Escreva o texto do fechamento antes de finalizar.',
  pedido_em_andamento: 'Há pedido de Terraform em andamento neste chamado. Espere terminar.',
  estado_invalido_para_finalizar: 'O chamado não está em atendimento.',
  nao_pode_assumir: 'Você não pode assumir este chamado para finalizá-lo.',
  comentario_publicado_sem_finalizar:
    'O comentário foi publicado, mas o chamado não pôde ser finalizado. Finalize pela tela do chamado.',
  sem_permissao_de_finalizar: 'Você não tem permissão de finalizar este chamado.',
  chamado_fora_do_escopo: 'Este chamado não está no seu escopo.',
  texto_longo: 'O texto do fechamento passou de 20 mil caracteres. Encurte antes de finalizar.',
  action_not_pending: 'Esta sugestão já foi aplicada ou recusada.',
  falha_ao_finalizar:
    'Não foi possível finalizar. Confira o chamado na tela antes de tentar de novo: o comentário pode já ter saído.',
}

const MOTIVOS_DE_FALHA: Record<string, string> = {
  comentario_publicado_sem_finalizar: ERROS.comentario_publicado_sem_finalizar,
  falha_ao_finalizar: ERROS.falha_ao_finalizar,
}

/** O `detail` de recusa é `{codigo, ...}`; o de gate é string. Devolve o código ou a mensagem. */
function codigoDoErro(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object' && typeof (detail as { codigo?: unknown }).codigo === 'string') {
    return (detail as { codigo: string }).codigo
  }
  return formatApiError(err, 'Falha ao finalizar o chamado.')
}

export function ChamadoFinalizarApplyCard({ conversationId, messageId, proposedAction }: Props) {
  const { status, params } = proposedAction
  const can = useCan()
  const canFinalize = can('chamados', 'assign')
  const proposto = typeof params.texto === 'string' ? params.texto : ''
  const [texto, setTexto] = useState(proposto)

  const applyMutation = useApplyChamadoAction(conversationId)
  const declineMutation = useDeclineAction(conversationId)

  const isPending = status === 'pending'
  const ticketRef = params.task_request_id ? `#${params.task_request_id}` : 'chamado'
  const emBranco = texto.trim() === ''

  const finalizar = () =>
    applyMutation.mutate(
      { messageId, texto },
      {
        onError: (err: unknown) => {
          const raw = codigoDoErro(err)
          notifications.show({ color: 'red', title: 'Erro', message: ERROS[raw] ?? raw })
        },
      },
    )

  return (
    <Card withBorder padding="sm" radius="md" style={{ maxWidth: 480 }}>
      <Group gap="xs" mb="xs" wrap="nowrap">
        <IconCircleCheck size={16} />
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          {`Fechamento sugerido para o ${ticketRef}`}
        </Text>
        <Badge size="xs" color={STATUS_COLORS[status]} variant="light">
          {STATUS_LABELS[status]}
        </Badge>
      </Group>

      {isPending ? (
        <Textarea
          aria-label="Texto do fechamento"
          value={texto}
          onChange={(e) => setTexto(e.currentTarget.value)}
          autosize
          minRows={4}
          maxRows={14}
          mb="xs"
        />
      ) : (
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }} mb="xs">
          {proposto}
        </Text>
      )}

      {status === 'failed' && (
        <Text size="xs" c="red" mb="xs">
          {MOTIVOS_DE_FALHA[String((proposedAction as { result?: { motivo?: unknown } }).result?.motivo ?? '')] ??
            'Não foi possível finalizar por aqui. Finalize pela tela do chamado.'}
        </Text>
      )}

      <Text size="xs" c="dimmed" mb="xs">
        Vai para o solicitante como comentário, e o chamado fica aguardando a validação dele.
      </Text>

      {isPending && (
        <Group gap="xs" mt="xs" wrap="nowrap">
          <Tooltip
            label="Requer permissão de atribuir chamados"
            disabled={canFinalize}
            withArrow
            position="top"
          >
            <div>
              <Button
                size="xs"
                color="inovareTeal"
                variant="filled"
                disabled={!canFinalize || emBranco}
                loading={applyMutation.isPending}
                onClick={finalizar}
              >
                Finalizar
              </Button>
            </div>
          </Tooltip>
          <Button
            size="xs"
            color="red"
            variant="light"
            loading={declineMutation.isPending}
            onClick={() => declineMutation.mutate({ messageId })}
          >
            Recusar
          </Button>
        </Group>
      )}
    </Card>
  )
}
